import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useStudents } from '../../Students/hooks/useStudents';
import { usePayments } from '../hooks/usePayments';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { SCHOOL_FEES, DISCOUNT_AMOUNT, PTA_CATEGORY, isDiscount, isWaived } from '../../../shared/constant/schoolFees';
import { getSchoolYearOptions, CURRENT_SCHOOL_YEAR } from '../../../shared/constant/schoolYears';
import { showSuccessToast } from '../../../shared/utils/dialogs';
import PaymentReviewModal from './PaymentReviewModal';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  student_id: '',
  payer_name: '',
  grade_level: GRADE_LEVELS[0],
  school_year: CURRENT_SCHOOL_YEAR,
  or_number: '',
  payment_date: todayISO(),
  status: 'Full',
  remarks: '',
};

function PaymentFormModal({ payment, onClose, onSubmit }) {
  const isEditing = Boolean(payment);
  const { students } = useStudents();
  const { payments } = usePayments();
  const [form, setForm] = useState(() => (payment ? { ...emptyForm, ...payment } : emptyForm));
  const [selectedCategories, setSelectedCategories] = useState(() =>
    payment?.category ? [payment.category] : []
  );
  const [error, setError] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [isStudentListOpen, setIsStudentListOpen] = useState(false);
  const [reviewRows, setReviewRows] = useState(null);
  const [discountChecked, setDiscountChecked] = useState(false);

  const showCategoryPicker = form.status === 'Partial' || isEditing;

  const paidCategoriesForStudent = useMemo(() => {
    if (!form.student_id) return new Set();
    return new Set(
      payments
        .filter((p) => p.student_id === form.student_id && p.id !== payment?.id)
        .map((p) => p.category)
    );
  }, [payments, form.student_id, payment?.id]);

  const availableFees = SCHOOL_FEES.filter((f) => !paidCategoriesForStudent.has(f.category));

  // Editing a waived PTA line keeps it at ₱0 instead of resetting it to the full fee.
  const amountForCategory = (category) => {
    if (isEditing && isWaived(payment) && category === payment.category) return 0;
    const fee = SCHOOL_FEES.find((f) => f.category === category);
    return fee ? fee.amount : Number(payment?.amount ?? 0);
  };

  const partialTotal = selectedCategories.reduce((sum, category) => sum + amountForCategory(category), 0);

  // The discount excludes the PTA contribution from what the student pays. It is only offered
  // while the PTA fee is still unpaid, so it can only be used once per student.
  const availableTotal = availableFees.reduce((sum, f) => sum + f.amount, 0);
  const ptaFee = availableFees.find((f) => f.category === PTA_CATEGORY)?.amount ?? 0;
  const hasEarlierDiscount =
    Boolean(form.student_id) &&
    payments.some(
      (p) => isDiscount(p) && p.student_id === form.student_id && p.school_year === form.school_year
    );
  const canDiscount = !isEditing && form.status === 'Full' && ptaFee > 0 && !hasEarlierDiscount;
  const useDiscount = discountChecked && canDiscount;

  const displayAmount =
    !isEditing && form.status === 'Full' ? availableTotal - (useDiscount ? ptaFee : 0) : partialTotal;

  useEffect(() => {
    if (!form.student_id || studentQuery) return;
    const linked = students.find((s) => s.id === form.student_id);
    if (linked) setStudentQuery(`${linked.firstname} ${linked.lastname}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, form.student_id]);

  useEffect(() => {
    setSelectedCategories((prev) => prev.filter((c) => !paidCategoriesForStudent.has(c)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.student_id]);

  const filteredStudents = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => `${s.firstname} ${s.lastname}`.toLowerCase().includes(q));
  }, [students, studentQuery]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    if (!isEditing) setSelectedCategories([]);
    setForm((prev) => ({ ...prev, status }));
  };

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      if (isEditing) return prev.includes(category) ? [] : [category];
      return prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category];
    });
  };

  const selectStudent = (student) => {
    setForm((prev) => ({
      ...prev,
      student_id: student.id,
      payer_name: `${student.firstname} ${student.lastname}`,
      grade_level: student.grade_level,
      school_year: student.school_year || prev.school_year,
    }));
    setStudentQuery(`${student.firstname} ${student.lastname}`);
    setIsStudentListOpen(false);
  };

  const clearStudent = () => {
    setForm((prev) => ({ ...prev, student_id: '', payer_name: '', grade_level: '' }));
    setStudentQuery('');
  };

  const handleReview = (e) => {
    e.preventDefault();
    setError('');

    if (!form.payer_name || !form.grade_level) {
      setError('Select a student to fill in Name and Grade Level');
      return;
    }

    const useAllFees = !isEditing && form.status === 'Full';
    if (useAllFees && availableFees.length === 0) {
      setError('This student has already paid for every fee category');
      return;
    }
    if (!useAllFees && selectedCategories.length === 0) {
      setError('Select at least one category');
      return;
    }

    const shared = {
      student_id: form.student_id || null,
      payer_name: form.payer_name,
      grade_level: form.grade_level,
      school_year: form.school_year,
      or_number: form.or_number || null,
      payment_date: form.payment_date,
      status: form.status,
      remarks: form.remarks || null,
    };

    const waivedRemarks = [form.remarks, 'PTA waived (discount)'].filter(Boolean).join(' — ');
    const rows = useAllFees
      ? availableFees.map((fee) =>
          useDiscount && fee.category === PTA_CATEGORY
            ? { ...shared, category: fee.category, amount: 0, remarks: waivedRemarks }
            : { ...shared, category: fee.category, amount: fee.amount }
        )
      : selectedCategories.map((category) => ({ ...shared, category, amount: amountForCategory(category) }));

    setReviewRows(rows);
  };

  const handleConfirm = async () => {
    await onSubmit(reviewRows);
    showSuccessToast(payment ? 'Payment updated successfully' : 'Payment recorded successfully');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />
      <form
        onSubmit={handleReview}
        className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[70vh] overflow-hidden flex flex-col"
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-navy-light">
          <h2 className="text-lg font-semibold text-white">{payment ? 'Edit Payment' : 'Record Payment'}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-200 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{error}</p>
          )}

          <div className="space-y-1 relative">
            <label className="text-sm font-medium text-gray-700">Student</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={studentQuery}
                onChange={(e) => {
                  setStudentQuery(e.target.value);
                  setIsStudentListOpen(true);
                  if (form.student_id)
                    setForm((prev) => ({ ...prev, student_id: '', payer_name: '', grade_level: '' }));
                }}
                onFocus={() => setIsStudentListOpen(true)}
                onBlur={() => setTimeout(() => setIsStudentListOpen(false), 150)}
                placeholder="Search student by name…"
                className="w-full rounded-md border border-gray-300 pl-9 pr-16 py-2 text-lg"
              />
              {studentQuery && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearStudent}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>

            {isStudentListOpen && (
              <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                {filteredStudents.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">No matching students</li>
                ) : (
                  filteredStudents.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectStudent(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      >
                        {s.lastname}, {s.firstname} ({s.grade_level})
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Name</label>
              <input
                required
                readOnly
                value={form.payer_name}
                placeholder="Select a student above"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg bg-gray-100 text-gray-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Grade Level</label>
              <select
                required
                disabled
                value={form.grade_level}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg bg-gray-100 text-gray-500"
              >
                {GRADE_LEVELS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">School Year</label>
            <select
              required
              value={form.school_year}
              onChange={set('school_year')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            >
              {getSchoolYearOptions().map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={form.status}
              onChange={handleStatusChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            >
              <option value="Full">Full</option>
              <option value="Partial">Partial</option>
            </select>
            {!isEditing && form.status === 'Full' && (
              <p className="text-xs text-gray-500">
                {form.student_id && paidCategoriesForStudent.size > 0
                  ? `${availableFees.length} of ${SCHOOL_FEES.length} fee categories remain unpaid for this student and will be recorded as separate paid line items.`
                  : `All ${SCHOOL_FEES.length} fee categories will be recorded as separate paid line items.`}
              </p>
            )}
            {!isEditing && form.status === 'Full' && (
              <label
                className={`mt-2 flex items-start gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm ${
                  canDiscount ? 'text-gray-700' : 'text-gray-400'
                }`}
              >
                <input
                  type="checkbox"
                  checked={useDiscount}
                  disabled={!canDiscount}
                  onChange={(e) => setDiscountChecked(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300"
                />
                <span>
                  <span className="font-medium">With ₱{DISCOUNT_AMOUNT} discount</span>
                  <span className="block text-xs text-gray-500">
                    {hasEarlierDiscount
                      ? 'A discount was already applied for this student this school year.'
                      : ptaFee === 0
                      ? 'The PTA contribution is already paid or waived for this student.'
                      : `Excludes the PTA contribution (₱${ptaFee}) from the total. PTA is recorded as ₱0 (waived).`}
                  </span>
                </span>
              </label>
            )}
          </div>

          {showCategoryPicker && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Category {isEditing ? '(choose one)' : '(choose one or more)'}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border border-gray-200 rounded-md p-3">
                {SCHOOL_FEES.map(({ category, amount }) => {
                  const isChecked = selectedCategories.includes(category);
                  const isPaid = paidCategoriesForStudent.has(category) && !isChecked;
                  return (
                    <label
                      key={category}
                      className={`flex items-center gap-2 text-sm ${
                        isPaid ? 'text-gray-400 line-through' : 'text-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isPaid}
                        onChange={() => toggleCategory(category)}
                        className="rounded border-gray-300"
                      />
                      {category} (₱{amount}) {isPaid && '— Paid'}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Amount (₱)</label>
              <input
                readOnly
                type="number"
                value={displayAmount}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg bg-gray-100 text-gray-500"
              />
              {useDiscount && (
                <p className="text-xs text-gray-500">
                  ₱{availableTotal.toLocaleString()} − ₱{ptaFee} PTA discount
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">OR Number</label>
              <input
                required
                value={form.or_number}
                onChange={set('or_number')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Payment Date</label>
            <input
              required
              type="date"
              value={form.payment_date}
              onChange={set('payment_date')}
              className="w-full lg:max-w-full max-w-[315px] min-w-0 rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Remarks</label>
            <textarea
              value={form.remarks}
              onChange={set('remarks')}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-28 py-2 text-center text-lg font-medium text-gray-600 border border-gray-300 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-28 py-2 rounded-md bg-navy-light text-white text-lg font-medium text-center hover:bg-navy"
          >
            Review
          </button>
        </div>
      </form>

      {reviewRows && (
        <PaymentReviewModal
          rows={reviewRows}
          onBack={() => setReviewRows(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

export default PaymentFormModal;
